/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { FaUnlock } from "react-icons/fa";
import { GrUndo } from "react-icons/gr";
import { RiContactsLine } from "react-icons/ri";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilValue, useSetRecoilState } from "recoil";
import BlockIcon from "../../../public/bulk-actions/BlockIcon";
import DeleteIcon from "../../../public/bulk-actions/DeleteIcon";
import EditContactIcon from "../../../public/bulk-actions/EditContactIcon";
import SendEmailConfirmationIcon from "../../../public/bulk-actions/SendEmailConfirmationIcon";
import UnsubscribeIcon from "../../../public/icons/popOver/UnsubscribeIcon";
import { ContactApi } from "../../api/Contacts";
import { ContactsAtom } from "../../atoms/ContactsAtom";
import { ContactsPerPageAtom } from "../../atoms/ContactsPerPageAtom";
import { TagsAtom } from "../../atoms/TagsAtom";
import { UpdateContactsFlagAtom } from "../../atoms/UpdateContactsAtom";
import { UserAtom } from "../../atoms/UserAtom";
import { ICategoryType } from "../../generated/Api";
import useClickOutside from "../../hook/useClickOutside";
import { Flex, notSelect } from "../../styles/general";
import { ContactStatus, IContact } from "../../types/Contact";
import { INTERNAL_UIDS_TO_SEE_CRM } from "../../utils/constants";
import { localStorageGetItem } from "../../utils/LocalStorage";
import IconBerserkerMail from "../Icon";
import Loading from "../Loading";
import AddContact from "../Modal/container/AddContact";
import { UnsuppressContactModal } from "../Modal/container/UnsuppressContact";
import { useModal } from "../Modal/Modal";
import Tag from "../Tag";
import Warning from "./../Modal/container/Warning";
import BlockContactModal from "./BlockContactModal";
import UnblockContactModal from "./UnblockContactModal";

const Container = styled.div`
  position: relative;
  margin-left: 5px;
  color: #2f2f2f;
  ${notSelect}
`;

interface PopoverProps {
  bottomPopover: boolean;
}

const Popover = styled.div<PopoverProps>`
  height: fit-content;
  width: 240px;

  padding: 10px;

  position: absolute;
  z-index: 20;

  background: white;
  box-shadow: 0px 3px 6px #00000026;

  top: ${(props) => (props.bottomPopover ? "24px" : "auto")};
  right: -4px;

  overflow: auto;

  border-radius: 5px;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;

  display: flex;
  flex-direction: column;

  cursor: default;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;

  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;
  }
`;

const Icon = styled.img`
  width: 15px;
  height: 15px;
  cursor: pointer;
`;

const OptionIcon = styled.img`
  width: 15px;
  height: 15px;
  margin-right: 5px;
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 11px;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -10px;
  justify-content: center;
`;

const IconRightText = styled.div`
  margin-left: 8px;
`;

const IconCss = css`
  svg {
    width: 15px;
    height: 15px;

    color: #70707080;
  }

  :hover {
    svg {
      color: white;
    }
  }
`;

const STATUS_TO_SEND_CONFIRMATION = [
  ContactStatus.NOT_CONFIRMED,
  ContactStatus.PENDING_CONFIRMATION,
  ContactStatus.SUSPENDED,
  ContactStatus.BOT_FILLED,
];

interface Props {
  contact: IContact;
  hasEmailConfirmation?: boolean;
  resetSearch: () => void;
}

export default function MoreActionsPopOver(props: Props): JSX.Element {
  const { contact, hasEmailConfirmation, resetSearch } = props;
  const setContacts = useSetRecoilState(ContactsAtom);
  const user = useRecoilValue(UserAtom);
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const [bottomPopover, setBottomPopover] = useState(true);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const tags = useRecoilValue(TagsAtom);
  const modal = useModal();
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTestDriveLoading, setTestDriveLoading] = useState(false);
  const setUpdateContactsFlag = useSetRecoilState(UpdateContactsFlagAtom);
  const setContactsOnPage = useSetRecoilState(ContactsPerPageAtom);
  const [isToShowCRM, setIsToShowCRM] = useState(false);

  useEffect(() => {
    let internalUid = localStorageGetItem("INTERNAL_USER_UID") as string;

    if (INTERNAL_UIDS_TO_SEE_CRM.includes(internalUid)) setIsToShowCRM(true);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    let bottom = containerRef.current.getBoundingClientRect().bottom;

    if (!bottomPopover)
      bottom =
        bottom + containerRef.current.getBoundingClientRect().height + 20;

    setBottomPopover(bottom >= 0 && bottom <= window.innerHeight);
  }, [containerRef, openDropView]);

  async function handleSendConfirmation() {
    const response = await ContactApi.sendConfirmation({
      contactIds: [contact._id],
    });
    if (response) {
      setContacts(
        (contacts) =>
          contacts &&
          contacts.map((c) => ({
            ...c,
            ...(c._id === contact._id && {
              status: ContactStatus.PENDING_CONFIRMATION,
            }),
          }))
      );

      toast("Email confirmation sent", { type: "success" });
    }
  }

  function handleUnsubscribeClick() {
    if (contact.status !== ContactStatus.ADMIN_UNSUBSCRIBED) {
      modal.pushModal(
        <Warning
          icon={"./icons/emailReport/nounsubscribe.svg"}
          title="Unsubscribe"
          description={`Are you sure you want unsubscribe this contact?`}
          buttonsName={["No", "Yes"]}
          onOkClicked={async () => {
            const response = await ContactApi.manyAdminUnsubscribed({
              contactIds: [contact._id],
            });

            if (response) {
              setContacts(
                (contacts) =>
                  contacts &&
                  contacts.map((c) => {
                    if (c._id === contact._id)
                      return {
                        ...c,
                        status: ContactStatus.ADMIN_UNSUBSCRIBED,
                      };
                    else return c;
                  })
              );
            }
            modal.popModal();
          }}
          onCancelClicked={() => {
            modal.popModal();
          }}
        />
      );
    } else {
      modal.pushModal(
        <Warning
          icon={"./icons/emailReport/nounsubscribe.svg"}
          title="Resubscribe"
          description={`Are you sure you want to resubscribe this unsubscribed contact?`}
          buttonsName={["No", "Yes"]}
          onOkClicked={async () => {
            const response = await ContactApi.manyRevertAdminUnsubscribe({
              contactIds: [contact._id],
            });

            if (response) {
              setContacts(
                (contacts) =>
                  contacts &&
                  contacts.map((c) => {
                    if (c._id === contact._id)
                      return {
                        ...c,
                        status: ContactStatus.NOT_CONFIRMED,
                      };
                    else return c;
                  })
              );
            }
            modal.popModal();
          }}
          onCancelClicked={() => {
            modal.popModal();
          }}
        />
      );
    }
  }

  async function handleDeleteClick() {
    const userProptectedTags = user.options.protectedTags || [];

    const contactProtectedTags = contact.tags
      .filter((t) => userProptectedTags.includes(t._id))
      .map((t) => tags.find((tg) => tg._id === t._id))
      .filter((t) => !!t);

    const foundTestDriveContactTag = tags.find((t) => {
      return t.name === "Testdrive Contact" && t.type === ICategoryType.System;
    });

    const testDriveTag = contact.tags.find(
      (ct) => ct._id === foundTestDriveContactTag?._id
    );

    if (contactProtectedTags.length) {
      modal.pushModal(
        <Warning
          icon={"./icons/delete.svg"}
          title="Delete Contact"
          description={
            <>
              This contact cannot be deleted due to the following protection
              tags:
              <Tags>
                {contactProtectedTags.map((elem) => {
                  return (
                    <Tag key={`flex_tags_any_${elem?._id}`} tag={elem as any} />
                  );
                })}
              </Tags>
            </>
          }
          buttonsName={["Ok"]}
          onOkClicked={() => {
            modal.popModal();
          }}
          onCancelClicked={() => {
            modal.popModal();
          }}
        />
      );
    } else {
      modal.pushModal(
        <Warning
          icon={"./icons/delete.svg"}
          title="Delete Contact"
          description="Are you sure you want to delete this contact?"
          buttonsName={["No", "Yes"]}
          onOkClicked={async () => {
            if (testDriveTag) {
              toast("You can't delete the Test Drive Contact", {
                type: "error",
              });
              modal.popModal();
            } else {
              try {
                const response = await ContactApi.remove(contact._id);

                if (response) {
                  toast("Success", {
                    type: "success",
                  });
                  setContactsOnPage((c) => c - 1);
                  setUpdateContactsFlag(true);
                  setContacts((contacts) =>
                    contacts
                      ? contacts.filter((c) => c._id !== contact._id)
                      : []
                  );
                }
                modal.popModal();
              } catch (e) {
                modal.popModal();
                console.error(e);
              }
            }
          }}
          onCancelClicked={() => modal.popModal()}
        />
      );
    }
  }

  function handleUnsuppressClick() {
    modal.pushModal(<UnsuppressContactModal suppressedContacts={[contact]} />);
  }

  function handleEditClick() {
    modal.pushModal(
      <AddContact isEdit contact={contact} />,
      contact ? "Edit contact" : "Add new contact"
    );
  }

  function handleDetailsClick() {
    router.push(`contacts/details/?contactId=${contact._id}`);
  }

  async function handleSetTestDrive() {
    if (isTestDriveLoading) return;
    try {
      setTestDriveLoading(true);
      await ContactApi.setDefaultTestDrive({ contactId: contact._id });
      resetSearch();
      toast("Success", { type: "success" });
    } catch (err) {
      console.log("Error", err);
      toast("Unexpected Error", { type: "error" });
    } finally {
      setTestDriveLoading(false);
    }
  }

  function handleBlockClick() {
    modal.pushModal(
      <BlockContactModal contacts={[contact]} />,
      "Block contact"
    );
  }

  function handleUnblockClick() {
    modal.pushModal(
      <UnblockContactModal contact={contact} />,
      "Unblock contact"
    );
  }

  return (
    <Container ref={wrapperRef}>
      {isTestDriveLoading && (
        <div
          style={{
            height: "100vh",
            width: "100vw",
            position: "fixed",
            zIndex: 9999,
            top: "0",
            left: "0",
            background: "rgb(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Loading />
        </div>
      )}
      <Flex
        style={{ height: "30px", cursor: "pointer" }}
        data-tip
        data-for="moreActions"
        onClick={() => setOpenDropView(!openDropView)}
      >
        <ReactTooltip
          id="moreActions"
          place="bottom"
          effect="solid"
          globalEventOff="click"
          backgroundColor="#333333"
          css={css`
            width: fit-content !important;
            padding: 8px 12px !important;
          `}
        >
          <TooltipText>More Actions</TooltipText>
        </ReactTooltip>

        <div
          css={css`
            cursor: pointer;
          `}
          onClick={() => setOpenDropView(!openDropView)}
        >
          <IconBerserkerMail name="threedots" />
        </div>
      </Flex>

      {openDropView && (
        <Popover ref={containerRef} bottomPopover={bottomPopover}>
          <Title>ACTIONS</Title>

          {/* {checkTestDrive(user?.billing) && (
            <Row css={IconCss} onClick={handleSetTestDrive}>
              <IconBerserkerMail name="tag" />
              <IconRightText style={{ fontWeight: "bold" }}>
                Set as Test Drive
              </IconRightText>
            </Row>
          )} */}

          {/* <Row css={IconCss} onClick={handleDetailsClick}>
            <RiUserSearchLine />
            <IconRightText>View details</IconRightText>
          </Row> */}

          {/* <Row css={IconCss} onClick={handleEditClick}>
            <EditContactIcon />
            <IconRightText>Edit contact details</IconRightText>
          </Row> */}

          {isToShowCRM ? (
            <Link
              href={{
                pathname: "/contacts/details",
                query: { contactId: contact._id },
              }}
            >
              <Row css={IconCss}>
                <EditContactIcon />
                <IconRightText>Edit contact details</IconRightText>
              </Row>
            </Link>
          ) : (
            <Row css={IconCss} onClick={handleEditClick}>
              <EditContactIcon />
              <IconRightText>Edit contact details</IconRightText>
            </Row>
          )}

          {STATUS_TO_SEND_CONFIRMATION.includes(contact.status) && (
            <Row css={IconCss} onClick={handleSendConfirmation}>
              <SendEmailConfirmationIcon />

              <IconRightText>
                {contact.status === ContactStatus.PENDING_CONFIRMATION
                  ? "Resend email confirmation"
                  : "Send email confirmation"}
              </IconRightText>
            </Row>
          )}

          {contact.status !== ContactStatus.UNSUBSCRIBED && (
            <Row css={IconCss} onClick={handleUnsubscribeClick}>
              {contact.status !== ContactStatus.ADMIN_UNSUBSCRIBED ? (
                <UnsubscribeIcon />
              ) : (
                <GrUndo />
              )}
              <IconRightText>
                {contact.status !== ContactStatus.ADMIN_UNSUBSCRIBED
                  ? "Unsubscribe contact"
                  : "Resubscribe"}
              </IconRightText>
            </Row>
          )}

          {contact.status === ContactStatus.SUSPENDED ? (
            <Row css={IconCss} onClick={handleUnblockClick}>
              <BlockIcon />
              <IconRightText>Unblock contact</IconRightText>
            </Row>
          ) : (
            <Row css={IconCss} onClick={handleBlockClick}>
              <BlockIcon />
              <IconRightText>Block contact</IconRightText>
            </Row>
          )}

          <Row css={IconCss} onClick={handleDeleteClick}>
            <DeleteIcon />
            <IconRightText>Delete contact</IconRightText>
          </Row>

          {/* {isToShowCRM && ( */}
          {/* <Link
            href={{
              pathname: "/contacts/details",
              query: { contactId: contact._id },
            }}
          >
            <Row css={IconCss}>
              <RiContactsLine />
              <IconRightText>CRM</IconRightText>
            </Row>
          </Link> */}
          {/* )} */}

          {contact.status === ContactStatus.SUPRESSED && (
            <Row css={IconCss} onClick={handleUnsuppressClick}>
              <FaUnlock />
              <IconRightText>Unsuppress contact</IconRightText>
            </Row>
          )}
        </Popover>
      )}
    </Container>
  );
}
